import { useId } from 'react';

export type ChartKind = 'line' | 'bar' | 'donut' | 'heatmap';

export interface ChartProps {
  kind: ChartKind;
  data: { label?: string; value: number }[];
  width?: number;
  height?: number;
  color?: string;
}

export function Chart({
  kind, data, width = 480, height = 160, color = 'var(--accent)',
}: ChartProps) {
  const id = useId();
  const max = Math.max(...data.map((d) => d.value), 1);

  if (!data.length) {
    return <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>—</div>;
  }

  if (kind === 'line') {
    const stepX = data.length > 1 ? (width - 20) / (data.length - 1) : 0;
    const points = data
      .map((d, i) => `${10 + i * stepX},${height - 10 - (d.value / max) * (height - 30)}`)
      .join(' ');
    return (
      <svg width="100%" viewBox={`0 0 ${width} ${height}`} aria-labelledby={`title-${id}`}>
        <title id={`title-${id}`}>line chart</title>
        <polyline points={points} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
        {data.map((d, i) => (
          <circle key={i} cx={10 + i * stepX} cy={height - 10 - (d.value / max) * (height - 30)} r={2.5} fill={color} />
        ))}
      </svg>
    );
  }

  if (kind === 'bar') {
    const barWidth = (width - 20) / data.length - 4;
    const slotWidth = barWidth + 4;
    const maxLabelChars = Math.max(...data.map((d) => d.label?.length ?? 0));
    // fontSize 9 → כ-6px לתו; מדלגים על תוויות כשאין להן מקום
    const labelStep = Math.max(1, Math.ceil((maxLabelChars * 6 + 6) / slotWidth));
    return (
      <svg width="100%" viewBox={`0 0 ${width} ${height}`}>
        {data.map((d, i) => {
          const h = (d.value / max) * (height - 30);
          return (
            <g key={i}>
              <rect
                x={10 + i * (barWidth + 4)}
                y={height - 10 - h}
                width={barWidth}
                height={h}
                fill={color}
                rx={3}
              />
              {d.label && i % labelStep === 0 && (
                <text
                  x={10 + i * (barWidth + 4) + barWidth / 2}
                  y={height - 2}
                  fontSize={9}
                  fill="var(--muted)"
                  textAnchor="middle"
                >
                  {d.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    );
  }

  if (kind === 'donut') {
    const total = data.reduce((acc, d) => acc + d.value, 0) || 1;
    const cx = width / 2;
    const cy = height / 2;
    const r = Math.min(width, height) / 2 - 10;
    let angle = -Math.PI / 2;
    const palette = ['var(--accent)', 'var(--ok)', 'var(--warn)', 'var(--info)', 'var(--danger)', 'var(--busy)'];

    return (
      <svg width="100%" viewBox={`0 0 ${width} ${height}`}>
        {data.map((d, i) => {
          const slice = (d.value / total) * Math.PI * 2;
          const x1 = cx + Math.cos(angle) * r;
          const y1 = cy + Math.sin(angle) * r;
          angle += slice;
          const x2 = cx + Math.cos(angle) * r;
          const y2 = cy + Math.sin(angle) * r;
          const large = slice > Math.PI ? 1 : 0;
          return (
            <path
              key={i}
              d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`}
              fill={palette[i % palette.length]}
            />
          );
        })}
        <circle cx={cx} cy={cy} r={r * 0.55} fill="var(--surface)" />
        <text x={cx} y={cy + 4} fontSize={14} fontWeight={600} fill="var(--text)" textAnchor="middle">
          {total}
        </text>
      </svg>
    );
  }

  // heatmap (fallback)
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${data.length}, 1fr)`, gap: 2 }}>
      {data.map((d, i) => (
        <div
          key={i}
          style={{
            height: 24,
            background: color,
            opacity: 0.2 + (d.value / max) * 0.8,
            borderRadius: 'var(--r-1)',
          }}
          title={`${d.label ?? ''}: ${d.value}`}
        />
      ))}
    </div>
  );
}

export function Spark({ values, color = 'var(--accent)' }: { values: number[]; color?: string }) {
  return <Chart kind="line" data={values.map((v) => ({ value: v }))} height={40} color={color} />;
}

export function Progress({ value, max = 100, color = 'var(--accent)' }: { value: number; max?: number; color?: string }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div style={{ height: 6, background: 'var(--surface-2)', borderRadius: 'var(--r-pill)', overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, transition: 'width 200ms ease' }} />
    </div>
  );
}

export function Ring({ value, max = 100, size = 48, color = 'var(--accent)' }: { value: number; max?: number; size?: number; color?: string }) {
  const r = size / 2 - 4;
  const c = 2 * Math.PI * r;
  const pct = Math.min(1, Math.max(0, value / max));

  return (
    <svg width={size} height={size}>
      <circle cx={size / 2} cy={size / 2} r={r} stroke="var(--surface-2)" strokeWidth={4} fill="none" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke={color}
        strokeWidth={4}
        fill="none"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - pct)}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}
